/**
 * REST client for the academy CRM (F3-I). Same as billing: SERVER-ONLY data
 * (no PowerSync), via `@/lib/rest`'s shared transport, wrapped in TanStack Query.
 */
import { createSafeQuery } from "@/lib/api";
import { createMutation, useQueryClient } from "@tanstack/solid-query";
import { queryKeys } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/crm";

// ─── Types (mirror of crm.rs's DTOs) ──────────────────────────────────────────

/**
 * Pipeline stages, in funnel order. Mirror of `crm::catalog::STAGES`
 * (Rust), which is the copy guarded against `lead_stage_check`. The CRM
 * carries no `#[utoipa::path]`, so it is absent from `openapi.json` and
 * `pnpm gen:api-types` cannot generate this — it is maintained by hand.
 */
export const STAGES = ["new", "contacted", "visit", "enrolled", "lost"] as const;
export type Stage = (typeof STAGES)[number];

/** The stage that means "this lead became a student". */
export const STAGE_ENROLLED: Stage = "enrolled";

/**
 * Sources a PERSON can pick when creating a lead by hand.
 *
 * Kept apart from the automatic ones on purpose: letting the front desk
 * choose `signup` would forge the very attribution the funnel exists to
 * measure. Only the new-lead dialog should offer this list.
 */
export const MANUAL_SOURCES = [
  "landing",
  "walk_in",
  "referral",
  "instagram",
  "whatsapp",
  "other",
] as const;

/** Origins the SERVER writes (`crm::funnel`), never a person. */
export const AUTOMATIC_SOURCES = ["signup", "free_enrollment"] as const;

/**
 * Every source the column accepts — `lead_source_check` after migration
 * 00090. Use it to READ (render a label, offer a filter); to offer a
 * CHOICE use {@link MANUAL_SOURCES}.
 *
 * These two were the same list until 00090 added the automatic origins,
 * and the client was never updated: leads from self-signup rendered with
 * an undefined label and could not be filtered for.
 */
export const ALL_SOURCES = [...MANUAL_SOURCES, ...AUTOMATIC_SOURCES] as const;
export type Source = (typeof ALL_SOURCES)[number];
export type SegmentTargetKind = "leads" | "students";

export interface Lead {
  id: string;
  /**
   * NULL when the account that originated this lead was deleted: account
   * deletion anonymizes the CRM row (migration 00107). The tombstone label
   * is set by the UI, not by the database.
   */
  name: string | null;
  /** NULL for leads from the automatic funnel: an open sign-up gives no phone. */
  phone?: string | null;
  email?: string | null;
  cedula?: string | null;
  interest?: string | null;
  interestCourseId?: string | null;
  stage: Stage;
  source: Source;
  assignedTo?: string | null;
  convertedUserId?: string | null;
  utmSource?: string | null;
  utmCampaign?: string | null;
  landingPath?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadEvent {
  id: string;
  kind: "note" | "call" | "whatsapp" | "email" | "visit" | "stage_change" | "created";
  body?: string | null;
  meta: Record<string, unknown>;
  actorUserId?: string | null;
  createdAt: string;
}

export interface SegmentCondition {
  field: string;
  op: "eq" | "neq" | "gt" | "lt" | "contains" | "in";
  value: unknown;
}
export interface SegmentDefinition {
  all: SegmentCondition[];
}
export interface Segment {
  id: string;
  organizationId?: string | null;
  name: string;
  target: SegmentTargetKind;
  definition: SegmentDefinition;
}

export interface WhatsappTemplate {
  id: string;
  organizationId?: string | null;
  key: string;
  name: string;
  body: string;
}

export interface Funnel {
  byStage: { stage: Stage; count: number }[];
  byMonth: { month: string; stage: Stage; count: number }[];
  total: number;
  conversionRate: number;
}
export interface SourceStat {
  source: Source;
  total: number;
  converted: number;
}

const qs = (params: Record<string, string | undefined>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) {
      sp.set(k, v);
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

// ─── Leads ─────────────────────────────────────────────────────────────────────

export interface LeadFilters {
  stage?: string;
  source?: string;
  assigned?: string;
  search?: string;
}

export const useLeads = (org: () => string | undefined, filters: () => LeadFilters) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.leads(org(), filters()),
    queryFn: () => apiJson<Lead[]>(`${BASE}/leads${qs({ org: org(), ...filters() })}`),
  }));

export const useLead = (org: () => string | undefined, leadId: () => string) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.lead(org(), leadId()),
    queryFn: () =>
      apiJson<{ lead: Lead; events: LeadEvent[] }>(`${BASE}/leads/${encodeURIComponent(leadId())}`),
  }));

export interface CreateLeadInput {
  org?: string;
  name: string;
  phone: string;
  email?: string;
  cedula?: string;
  interest?: string;
  interestCourseId?: string;
  source?: string;
  assignedTo?: string;
  notes?: string;
}

export const useCreateLead = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: CreateLeadInput) =>
      apiJson<{ id: string }>(`${BASE}/leads`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.leadsAll() }),
  }));
};

export interface PatchLeadInput {
  stage?: string;
  assignedTo?: string | null;
  name?: string;
  phone?: string;
  email?: string | null;
  cedula?: string | null;
  interest?: string | null;
  interestCourseId?: string | null;
  notes?: string | null;
}

export const usePatchLead = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (args: { id: string; patch: PatchLeadInput }) =>
      apiJson<{ ok: boolean }>(`${BASE}/leads/${encodeURIComponent(args.id)}`, {
        method: "PATCH",
        body: JSON.stringify(args.patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.crm.leadsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.crm.all });
    },
  }));
};

export const useAddEvent = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (args: {
      id: string;
      kind: string;
      body?: string;
      meta?: Record<string, unknown>;
    }) =>
      apiJson<{ id: string }>(`${BASE}/leads/${encodeURIComponent(args.id)}/events`, {
        method: "POST",
        body: JSON.stringify({ kind: args.kind, body: args.body, meta: args.meta }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.all }),
  }));
};

export interface ConvertInput {
  userId?: string;
  createUser?: { name: string; email: string; phone?: string; cedula?: string; campusId?: string };
}

export const useConvertLead = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (args: { id: string; input: ConvertInput }) =>
      apiJson<{ convertedUserId: string }>(`${BASE}/leads/${encodeURIComponent(args.id)}/convert`, {
        method: "POST",
        body: JSON.stringify(args.input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.crm.leadsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.crm.all });
    },
  }));
};

// ─── Segments ──────────────────────────────────────────────────────────────────

export const useSegments = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.segments(org()),
    queryFn: () => apiJson<Segment[]>(`${BASE}/segments${qs({ org: org() })}`),
  }));

export const useCreateSegment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      name: string;
      target: SegmentTargetKind;
      definition: SegmentDefinition;
    }) =>
      apiJson<{ id: string }>(`${BASE}/segments`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.segmentsAll() }),
  }));
};

export const useUpdateSegment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (args: { id: string; name?: string; definition?: SegmentDefinition }) =>
      apiJson<{ ok: boolean }>(`${BASE}/segments/${encodeURIComponent(args.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ name: args.name, definition: args.definition }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.segmentsAll() }),
  }));
};

export const useDeleteSegment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (id: string) =>
      apiJson<{ ok: boolean }>(`${BASE}/segments/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.segmentsAll() }),
  }));
};

export const usePreviewSegment = () =>
  createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      target: SegmentTargetKind;
      definition: SegmentDefinition;
    }) =>
      apiJson<{ count: number; sample: Record<string, unknown>[] }>(`${BASE}/segments/preview`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  }));

// ─── WhatsApp templates ──────────────────────────────────────────────────────

export const useTemplates = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.templates(org()),
    queryFn: () => apiJson<WhatsappTemplate[]>(`${BASE}/whatsapp-templates${qs({ org: org() })}`),
  }));

export const useCreateTemplate = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; key: string; name: string; body: string }) =>
      apiJson<{ id: string }>(`${BASE}/whatsapp-templates`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.templatesAll() }),
  }));
};

export const useUpdateTemplate = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (args: { id: string; name?: string; body?: string }) =>
      apiJson<{ ok: boolean }>(`${BASE}/whatsapp-templates/${encodeURIComponent(args.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ name: args.name, body: args.body }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.templatesAll() }),
  }));
};

export const useDeleteTemplate = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (id: string) =>
      apiJson<{ ok: boolean }>(`${BASE}/whatsapp-templates/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.crm.templatesAll() }),
  }));
};

// ─── Analytics ────────────────────────────────────────────────────────────────

export const useFunnel = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.funnel(org()),
    queryFn: () => apiJson<Funnel>(`${BASE}/analytics/funnel${qs({ org: org() })}`),
  }));

export const useSources = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.crm.sources(org()),
    queryFn: () => apiJson<SourceStat[]>(`${BASE}/analytics/sources${qs({ org: org() })}`),
  }));

// Static manifest of segment fields (mirror of Rust crm::segments' curated
// map). Feeds the UI's segment builder. Readable labels are provided by the
// component (i18n); only keys/types/operators go here.
export interface SegmentFieldSpec {
  field: string;
  type: "text" | "int" | "enum";
  ops: SegmentCondition["op"][];
  options?: string[];
}
export const SEGMENT_FIELDS: Record<SegmentTargetKind, SegmentFieldSpec[]> = {
  leads: [
    { field: "stage", type: "enum", ops: ["eq", "neq", "in"], options: [...STAGES] },
    // ALL sources, not just the hand-pickable ones: "everyone who signed up on
    // their own and has not paid" is a segment worth building, and with the
    // manual list it was impossible to express.
    { field: "source", type: "enum", ops: ["eq", "neq", "in"], options: [...ALL_SOURCES] },
    { field: "assigned_to", type: "text", ops: ["eq", "neq", "in"] },
    { field: "interest_course_id", type: "text", ops: ["eq", "neq", "in"] },
    { field: "interest", type: "text", ops: ["eq", "neq", "contains"] },
    { field: "created_days_ago", type: "int", ops: ["eq", "gt", "lt"] },
  ],
  students: [
    { field: "debt_usd_minor", type: "int", ops: ["eq", "gt", "lt"] },
    { field: "last_active_days", type: "int", ops: ["eq", "gt", "lt"] },
    {
      field: "cohort_status",
      type: "enum",
      ops: ["eq", "neq", "in"],
      options: ["pre_enrolled", "waitlisted", "active", "completed", "withdrawn"],
    },
    { field: "national_id", type: "text", ops: ["eq", "neq", "contains"] },
    { field: "campus_id", type: "text", ops: ["eq", "neq", "in"] },
  ],
};
